import { useState, useEffect, useRef } from 'react';
import { collection, addDoc, deleteDoc, doc, onSnapshot, serverTimestamp, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase/firestore';
import { uploadPhoto, deletePhoto } from '../firebase/storage';
import { useAuth } from '../context/AuthContext';
import { displayName } from '../utils/displayName';
import './PhotoGallery.scss';

const PhotoGallery = () => {
  const { user } = useAuth();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [selected, setSelected] = useState(new Set()); // ids of selected photos
  const [deleting, setDeleting] = useState(false);
  const [confirmBulk, setConfirmBulk] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const q = query(collection(db, 'gallery'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      setPhotos(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, () => setLoading(false));
    return unsub;
  }, []);

  const handleFiles = async (files) => {
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        if (!file.type.startsWith('image/')) continue;
        const { url, path } = await uploadPhoto(file, 'gallery');
        await addDoc(collection(db, 'gallery'), {
          url, path,
          uploadedBy: user.email,
          createdAt: serverTimestamp(),
        });
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const deleteOne = async (photo) => {
    await deletePhoto(photo.path);
    await deleteDoc(doc(db, 'gallery', photo.id));
  };

  const handleDeleteSelected = async () => {
    setDeleting(true);
    setConfirmBulk(false);
    try {
      await Promise.all(
        photos.filter(p => selected.has(p.id)).map(deleteOne)
      );
      setSelected(new Set());
      setEditMode(false);
    } finally {
      setDeleting(false);
    }
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selected.size === photos.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(photos.map(p => p.id)));
    }
  };

  const exitEditMode = () => {
    setEditMode(false);
    setSelected(new Set());
  };

  const navigate = (dir) => {
    const idx = photos.findIndex(p => p.id === selectedPhoto.id);
    const next = dir === 'next'
      ? photos[(idx + 1) % photos.length]
      : photos[(idx - 1 + photos.length) % photos.length];
    setSelectedPhoto(next);
  };

  return (
    <div className="photo-gallery">
      <div className="gallery-header">
        <div>
          <h2>Our Gallery</h2>
          <p className="gallery-count">{photos.length} {photos.length === 1 ? 'memory' : 'memories'}</p>
        </div>
        <div className="gallery-actions">
          {!editMode ? (
            <>
              <button className="btn-edit" onClick={() => setEditMode(true)} disabled={photos.length === 0}>
                Edit
              </button>
              <button className="btn-upload" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                {uploading ? 'Uploading...' : '+ Add Photos'}
              </button>
            </>
          ) : (
            <>
              <button className="btn-select-all" onClick={selectAll}>
                {selected.size === photos.length ? 'Deselect all' : 'Select all'}
              </button>
              <button
                className="btn-delete-selected"
                onClick={() => setConfirmBulk(true)}
                disabled={selected.size === 0 || deleting}
              >
                {deleting ? 'Deleting...' : `Delete${selected.size > 0 ? ` (${selected.size})` : ''}`}
              </button>
              <button className="btn-cancel-edit" onClick={exitEditMode}>Cancel</button>
            </>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={e => handleFiles(e.target.files)}
          />
        </div>
      </div>

      {loading ? (
        <div className="gallery-loading">Loading memories...</div>
      ) : photos.length === 0 ? (
        <div className="gallery-empty">
          <p>No photos yet.</p>
          <p>Upload your first memory!</p>
        </div>
      ) : (
        <div className={`gallery-grid ${editMode ? 'edit-mode' : ''}`}>
          {photos.map(photo => {
            const isSelected = selected.has(photo.id);
            return (
              <div
                key={photo.id}
                className={`gallery-item ${isSelected ? 'selected' : ''}`}
                onClick={() => editMode ? toggleSelect(photo.id) : setSelectedPhoto(photo)}
              >
                <img
                  src={photo.url}
                  alt="Memory"
                  style={{ opacity: 0 }}
                  onLoad={e => { e.target.style.opacity = 1; }}
                />
                {!editMode && (
                  <div className="gallery-overlay">
                    <span className="view-icon">♥</span>
                  </div>
                )}
                {editMode && (
                  <div className="select-overlay">
                    <div className={`select-check ${isSelected ? 'checked' : ''}`}>
                      {isSelected && '✓'}
                    </div>
                  </div>
                )}
                <div className="photo-uploader">{displayName(photo.uploadedBy)}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Lightbox (only when not in edit mode) */}
      {selectedPhoto && !editMode && (
        <div className="lightbox" onClick={() => setSelectedPhoto(null)}>
          <button className="lightbox-close" onClick={() => setSelectedPhoto(null)}>×</button>
          <button className="lightbox-nav lightbox-prev" onClick={e => { e.stopPropagation(); navigate('prev'); }}>‹</button>
          <img src={selectedPhoto.url} alt="Memory" onClick={e => e.stopPropagation()} />
          <button className="lightbox-nav lightbox-next" onClick={e => { e.stopPropagation(); navigate('next'); }}>›</button>
          <div className="lightbox-meta" onClick={e => e.stopPropagation()}>
            <span>{photos.findIndex(p => p.id === selectedPhoto.id) + 1} / {photos.length}</span>
            <span>by {displayName(selectedPhoto.uploadedBy)}</span>
          </div>
        </div>
      )}

      {/* Bulk delete confirm */}
      {confirmBulk && (
        <div className="confirm-overlay" onClick={() => setConfirmBulk(false)}>
          <div className="confirm-box" onClick={e => e.stopPropagation()}>
            <p>Delete {selected.size} {selected.size === 1 ? 'photo' : 'photos'}?</p>
            <div className="confirm-actions">
              <button className="btn-confirm-cancel" onClick={() => setConfirmBulk(false)}>Cancel</button>
              <button className="btn-confirm-delete" onClick={handleDeleteSelected}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoGallery;
